import { XMarkIcon } from '@heroicons/react/20/solid';
import {
  ExclamationCircleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
} from '@heroicons/react/24/outline';
import PropTypes from 'prop-types';

function DashboardBanner({
  type = 'warning',
  title,
  message,
  actionText,
  onAction,
  onDismiss,
  dismissible = true,
}) {
  const getIcon = () => {
    switch (type) {
      case 'danger':
        return (
          <ExclamationCircleIcon
            className="size-5 text-red-400"
            aria-hidden="true"
          />
        );
      case 'info':
        return (
          <InformationCircleIcon
            className="size-5 text-blue-400"
            aria-hidden="true"
          />
        );
      default:
        return (
          <ExclamationTriangleIcon
            className="size-5 text-yellow-400"
            aria-hidden="true"
          />
        );
    }
  };

  const getBackgroundColor = () => {
    switch (type) {
      case 'danger':
        return 'bg-red-50 border-red-400';
      case 'info':
        return 'bg-blue-50 border-blue-400';
      default:
        return 'bg-yellow-50 border-yellow-400';
    }
  };

  const getTextColor = () => {
    switch (type) {
      case 'danger':
        return 'text-red-700';
      case 'info':
        return 'text-blue-700';
      default:
        return 'text-yellow-700';
    }
  };

  const getActionButtonColor = () => {
    switch (type) {
      case 'danger':
        return 'bg-red-600 hover:bg-red-500 focus-visible:outline-red-600';
      case 'info':
        return 'bg-blue-600 hover:bg-blue-500 focus-visible:outline-blue-600';
      default:
        return 'bg-yellow-600 hover:bg-yellow-500 focus-visible:outline-yellow-600';
    }
  };

  return (
    <div className={`border-l-4 ${getBackgroundColor()} p-4`}>
      <div className="flex">
        <div className="shrink-0">{getIcon()}</div>
        <div className="ml-3 flex-1">
          {title && (
            <h3 className={`font-medium text-sm ${getTextColor()}`}>{title}</h3>
          )}
          {message && (
            <p className={`mt-1 text-sm ${getTextColor()}`}>{message}</p>
          )}
          {actionText && onAction && (
            <div className="mt-3">
              <button
                type="button"
                className={`inline-flex items-center rounded-md px-3 py-2 font-semibold text-sm text-white shadow-sm focus-visible:outline-2 focus-visible:outline-offset-2 ${getActionButtonColor()}`}
                onClick={onAction}
              >
                {actionText}
              </button>
            </div>
          )}
        </div>
        {dismissible && onDismiss && (
          <div className="ml-4 flex shrink-0">
            <button
              type="button"
              className={`inline-flex rounded-md p-1.5 focus-visible:outline-2 focus-visible:outline-offset-2 ${getTextColor()} hover:bg-white/20 focus-visible:outline-current`}
              onClick={onDismiss}
            >
              <span className="sr-only">Dismiss</span>
              <XMarkIcon className="size-5" aria-hidden="true" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

DashboardBanner.propTypes = {
  type: PropTypes.oneOf(['warning', 'danger', 'info']),
  title: PropTypes.string,
  message: PropTypes.string.isRequired,
  actionText: PropTypes.string,
  onAction: PropTypes.func,
  onDismiss: PropTypes.func,
  dismissible: PropTypes.bool,
};

export default DashboardBanner;
